/**
 * SourcingView — /sourcing
 *
 * 전사 소싱(니즈) 목록 페이지 (Phase 1).
 *   - 날짜(생성일) 그룹 + 검색(제품명/파트넘버)
 *   - 컬럼: 파트넘버 | 디스크립션 | 컨디션 | 수량 | 연결 딜 | 영업 요청 | 소싱 메모 | 매입처-가격 | ⋯
 *   - ⋯ 메뉴: 삭제(soft delete)
 *   - + 니즈 생성: 간단 모달(독립 니즈)
 *
 * (Phase 2+: 드래그 클러스터링, 매입처 지정/RFQ, 가격 popover — 후속)
 *
 * loader 패턴(ADR-0002) — main.ts 라우트가 fetchSourcingList 로 loader.
 *
 * 분해 구조 (M4, 2026-07-08 — deal-detail 분해 계획 v2 패턴 이식):
 *   sourcing/types.ts         state 정의 + factory + 도메인 상수
 *   sourcing/render-list.ts   리스트(날짜 섹션/클러스터/행/태그 편집/묶기 확인)
 *   sourcing/render-modals.ts 니즈 생성/매입처 지정 모달
 *   sourcing/render-price.ts  가격 응답 popover (body 포탈)
 *   이 파일 = 뷰 클래스: 이벤트 핸들러 + 데이터 로드 + 부분 재렌더 배선.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { escapeHtml, catalogHitDescription } from '../lib/cells';
import {
  emptyCreate, emptyPrice, emptySupplierModal, emptyVisibilityModal,
  type CreateState, type DragPayload, type SourcingData, type SourcingLoaded,
} from './sourcing/types';
import { renderBody } from './sourcing/render-list';
import { renderCreateModal, renderCreateSearchResultsHtml, renderSupplierListInner, renderSupplierModal, renderVisibilityModal } from './sourcing/render-modals';
import { renderAltResults, renderPricePopover } from './sourcing/render-price';

export type { SourcingLoaded } from './sourcing/types';

export class SourcingView extends SolaView<SourcingData> {
  private _searchTimer: number | undefined;
  private _abort: AbortController | undefined;

  override events = {
    click: {
      '@need-menu-open':   'handleMenuOpen',
      '@need-menu-close':  'handleMenuClose',
      '@need-delete':      'handleDelete',
      '@need-purchase-request': 'handlePurchaseRequest',
      '@copy-desc':        'handleCopyDesc',
      '@create-open':      'handleCreateOpen',
      '@create-close':     'handleCreateClose',
      '@create-submit':    'handleCreateSubmit',
      '@create-tag':       'handleCreateTag',
      '@create-clear':     'handleCreateClear',
      '@merge-apply':      'handleMergeApply',
      '@merge-cancel':     'handleMergeCancel',
      '@group-dissolve':   'handleGroupDissolve',
      '@group-remove':     'handleGroupRemove',
      '@tag-open':         'handleTagOpen',
      '@tag-close':        'handleTagClose',
      '@tag-toggle':       'handleTagToggle',
      '@supplier-open':    'handleSupplierOpen',
      '@supplier-close':   'handleSupplierClose',
      '@supplier-toggle':  'handleSupplierToggle',
      '@supplier-save':    'handleSupplierSave',
      '@visibility-open':   'handleVisibilityOpen',
      '@visibility-close':  'handleVisibilityClose',
      '@visibility-toggle': 'handleVisibilityToggle',
      '@visibility-save':   'handleVisibilitySave',
      '@price-open':       'handlePriceOpen',
      '@price-close':      'handlePriceClose',
      '@price-stock':      'handlePriceStock',
      '@price-tags-toggle': 'handlePriceTagsToggle',
      '@price-tag':        'handlePriceTag',
      '@price-alt-pick':   'handlePriceAltPick',
      '@price-save':       'handlePriceSave',
      '@price-purchase-toggle': 'handlePricePurchaseToggle',
    },
    input: {
      '@search-input':     'handleSearchInput',
      '@create-field':     'handleCreateField',
      '@create-search':    'handleCreateSearch',
      '@supplier-search':  'handleSupplierSearch',
      '@visibility-search': 'handleVisibilitySearch',
      '@price-field':      'handlePriceField',
      '@price-alt-input':  'handlePriceAltInput',
    },
    dblclick:  { '@create-pick': 'handleCreatePick' },   // 품목 검색 결과 더블클릭 → 선택
    dragstart: { '@drag-group': 'handleGroupDragStart', '@drag-need': 'handleDragStart', '@supplier-drag': 'handleSupplierDragStart' },
    dragover:  { '@drag-need': 'handleDragOver', '@supplier-drop': 'handleSupplierDragOver' },
    drop:      { '@drag-need': 'handleDrop', '@supplier-drop': 'handleSupplierDrop' },
    dragend:   { '@drag-need': 'handleDragEnd', '@supplier-drag': 'handleSupplierDragEnd' },
  };

  private _dragging: DragPayload | null = null;
  private _supDragId: string | null = null;   // 매입처 순서 드래그 중인 id
  private _dropIndicatorEl: HTMLElement | null = null;
  private _altTimer: number | undefined;          // 대체품목 검색 디바운스
  private _createSearchTimer: number | undefined; // 니즈 생성 품목 검색 디바운스
  // 가격 popover 를 document.body 직속으로 포탈 — 조상 containing-block(transform 등) 회피.
  private _priceLayer: HTMLElement | null = null;

  // 포탈된 popover 의 이벤트는 Sola 위임이 안 닿으므로 수동 바인딩.
  private _onPriceClick = (e: MouseEvent): void => {
    const el = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!el || !this._priceLayer?.contains(el)) return;
    switch (el.dataset.action) {
      case 'price-close':       this.handlePriceClose(); break;
      case 'price-stock':       this.handlePriceStock(); break;
      case 'price-tags-toggle': this.handlePriceTagsToggle(); break;
      case 'price-tag':         this.handlePriceTag(e, el); break;
      case 'price-alt-pick':    this.handlePriceAltPick(e, el); break;
      case 'price-save':        void this.handlePriceSave(); break;
      case 'price-purchase-toggle': void this.handlePricePurchaseToggle(e, el); break;
      case 'price-image-del':   void this.handlePriceImageDelete(e, el); break;
    }
  };
  private _onPriceInput = (e: Event): void => {
    const el = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!el || !this._priceLayer?.contains(el)) return;
    if (el.dataset.action === 'price-field')          this.handlePriceField(e, el);
    else if (el.dataset.action === 'price-alt-input') this.handlePriceAltInput(e, el);
    else if (el.dataset.action === 'price-image-pick') void this.handlePriceImagePick(e, el);
  };

  // 드래그 중 삽입 위치 표시는 재렌더 없이 DOM 직접 조작.
  private _clearDropIndicator(): void {
    this._dropIndicatorEl?.classList.remove('drop-above', 'drop-below', 'drop-merge');
    this._dropIndicatorEl = null;
  }

  constructor(loaded: SourcingLoaded) {
    super();
    this.data = {
      needs:       loaded.needs,
      nextCursor:  loaded.nextCursor,
      loadingMore: false,
      search:       '',
      loading:      false,
      menuId:       null,
      create:       emptyCreate(),
      supplierModal: emptySupplierModal(),
      visibilityModal: emptyVisibilityModal(),
      price:        emptyPrice(),
      tagEditor:    null,
      mergeConfirm: null,
    };
  }

  override getTitle(): string { return '소싱 — HCLTech'; }

  // ─── 무한 스크롤 (window scroll) ──────────────────────────────────
  private _onScroll = (): void => {
    if (!this.data.nextCursor || this.data.loadingMore) return;
    const nearBottom = window.innerHeight + window.scrollY
      >= document.documentElement.scrollHeight - 400;
    if (nearBottom) void this.loadMore();
  };
  override onMount(): void {
    window.addEventListener('scroll', this._onScroll, { passive: true });
    // 가격 popover 레이어를 body 직속에 생성(포탈) + 수동 이벤트 바인딩.
    // data-sola 를 수동 부여 — 포탈이라 컴포넌트 루트 밖이지만 같은 CSS 스코프
    // ([data-sola="SourcingView"])에 묶는다 (M4 CSS 스코핑 파일럿).
    this._priceLayer = document.createElement('div');
    this._priceLayer.className = 'sourcing-price-layer';
    this._priceLayer.setAttribute('data-sola', 'SourcingView');
    document.body.appendChild(this._priceLayer);
    this._priceLayer.addEventListener('click', this._onPriceClick);
    this._priceLayer.addEventListener('input', this._onPriceInput);
    this._priceLayer.addEventListener('change', this._onPriceInput);
  }
  override onUnmount(): void {
    window.removeEventListener('scroll', this._onScroll);
    if (this._searchTimer) window.clearTimeout(this._searchTimer);
    if (this._altTimer) window.clearTimeout(this._altTimer);
    this._abort?.abort();
    if (this._priceLayer) {
      this._priceLayer.removeEventListener('click', this._onPriceClick);
      this._priceLayer.removeEventListener('input', this._onPriceInput);
      this._priceLayer.removeEventListener('change', this._onPriceInput);
      this._priceLayer.remove();
      this._priceLayer = null;
    }
    super.onUnmount();
  }

  private async loadMore(): Promise<void> {
    const cursor = this.data.nextCursor;
    if (!cursor || this.data.loadingMore) return;
    this.data.loadingMore = true;
    this.rerenderBody();
    try {
      const r = await api.fetchSourcingList({ search: this.data.search, cursor });
      this.data.needs      = [...this.data.needs, ...r.needs];
      this.data.nextCursor = r.nextCursor;
    } catch { /* 유지 */ }
    this.data.loadingMore = false;
    this.rerenderBody();
  }

  // ─── 데이터 ───────────────────────────────────────────────────────
  private async reload(): Promise<void> {
    this._abort?.abort();                    // 직전 검색 요청 취소 (레이스 방지)
    const ac = new AbortController();
    this._abort = ac;
    this.data.loading = true;
    this.rerenderBody();
    try {
      const r = await api.fetchSourcingList({ search: this.data.search, signal: ac.signal });
      this.data.needs      = r.needs;
      this.data.nextCursor = r.nextCursor;   // 검색/새로고침 = 첫 페이지로 리셋
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;   // 더 새 요청에 양보
    }
    if (this._abort !== ac) return;          // 이미 더 새 요청이 시작됨
    this.data.loading = false;
    this.data.menuId  = null;
    this.rerenderBody();
  }

  /**
   * 리스트를 다시 불러오되 **현재 로드된 페이지 수와 스크롤 위치를 보존**한다.
   *
   * 일반 reload() 는 커서 없이 첫 페이지(20건)만 받아 needs 를 통째로 교체하므로,
   * 무한스크롤로 아래 페이지들을 로드해 둔 상태에서 호출하면 리스트가 첫 페이지로
   * 줄어들며 문서 높이가 급감 → 스크롤이 위로 튀고 편집하던 행이 사라진다.
   * (매입처 지정 저장 후 증상) 여기서는 이전 개수만큼 페이지를 이어 받은 뒤
   * 스크롤을 원위치로 되돌린다.
   */
  private async reloadKeepingScroll(): Promise<void> {
    this._abort?.abort();
    const ac = new AbortController();
    this._abort = ac;
    const prevCount = this.data.needs.length;
    const y = window.scrollY;
    try {
      const first = await api.fetchSourcingList({ search: this.data.search, signal: ac.signal });
      if (this._abort !== ac) return;
      let needs  = first.needs;
      let cursor = first.nextCursor;
      // 이전에 무한스크롤로 로드했던 만큼 페이지를 이어서 채운다.
      while (cursor && needs.length < prevCount) {
        const more = await api.fetchSourcingList({ search: this.data.search, cursor, signal: ac.signal });
        if (this._abort !== ac) return;
        needs  = needs.concat(more.needs);
        cursor = more.nextCursor;
      }
      this.data.needs      = needs;
      this.data.nextCursor = cursor;
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
    }
    if (this._abort !== ac) return;
    this.data.loading = false;
    this.data.menuId  = null;
    this.rerenderBody();
    // innerHTML 교체로 스크롤이 리셋되므로 원위치 복원 (문서 높이 동일 → 그대로 유지).
    window.scrollTo(0, y);
  }

  // ─── 부분 재렌더 배선 (state → render-* 순수 함수, Sola refreshRegion — M7) ──
  private rerenderBody(): void {
    this.refreshRegion('.sourcing-body', () => renderBody(this.data));
  }
  private rerenderModal(): void {
    this.refreshRegion('.sourcing-modal-wrap', () => renderCreateModal(this.data.create));
  }
  private rerenderVisibilityModal(): void {
    this.refreshRegion('.sourcing-visibility-modal-wrap', () => renderVisibilityModal(this.data.visibilityModal));
  }
  private rerenderSupplierModal(): void {
    this.refreshRegion('.sourcing-supplier-modal-wrap', () => renderSupplierModal(this.data.supplierModal));
  }
  private rerenderPricePopover(): void {
    // 포탈 레이어(root 밖) — element 직접 전달
    if (this._priceLayer) this.refreshRegion(this._priceLayer, () => renderPricePopover(this.data.price));
  }

  // ─── 드래그 묶기 (Sola events: dragstart/over/drop/end) ───────────
  private _startDrag(e: Event, payload: DragPayload): void {
    this._dragging = payload;
    const dt = (e as DragEvent).dataTransfer;
    if (dt) { dt.effectAllowed = 'move'; dt.setData('text/plain', '1'); }
  }
  handleDragStart(e: Event, target: HTMLElement): void {
    const id = target.dataset.needId;
    if (id) this._startDrag(e, { type: 'need', id });
  }
  handleGroupDragStart(e: Event, target: HTMLElement): void {
    const key = target.dataset.groupKey;
    if (key) this._startDrag(e, { type: 'group', key });
  }
  handleDragOver(e: Event, target: HTMLElement): void {
    const id = target.dataset.needId;
    const d  = this._dragging;
    if (!d || !id) return;
    if (d.type === 'need' && id === d.id) return;                 // 자기 자신
    if (d.type === 'group') {
      const tn = this.data.needs.find((n) => n.id === id);
      if (tn && tn.sourcing_group_key === d.key) return;          // 같은 그룹
    }
    e.preventDefault();   // drop 허용
    // 삽입 위치 미리보기 — 니즈=합치기 하이라이트, 그룹=커서 위치로 위/아래 라인.
    const cluster = target.closest('.sourcing-cluster') as HTMLElement | null;
    if (!cluster) return;
    this._clearDropIndicator();
    if (d.type === 'need') {
      cluster.classList.add('drop-merge');
    } else {
      const rect = cluster.getBoundingClientRect();
      const below = (e as DragEvent).clientY > rect.top + rect.height / 2;
      cluster.classList.add(below ? 'drop-below' : 'drop-above');
    }
    this._dropIndicatorEl = cluster;
  }
  async handleDrop(e: Event, target: HTMLElement): Promise<void> {
    e.preventDefault();
    this._clearDropIndicator();
    const targetId = target.dataset.needId;
    const source   = this._dragging;
    this._dragging = null;
    if (!source || !targetId) return;

    if (source.type === 'need') {
      // 개별 니즈 = 합치기(확인 팝업)
      if (source.id === targetId) return;
      const de = e as DragEvent;
      this.data.mergeConfirm = { source, targetId, x: de.clientX, y: de.clientY };
      this.rerenderBody();
      return;
    }
    // 그룹 = 이동(순서/재날짜). 커서가 대상 클러스터의 위/아래 어디인지로 위치 결정.
    const cluster = target.closest('.sourcing-cluster') as HTMLElement | null;
    let below = false;
    if (cluster) {
      const r = cluster.getBoundingClientRect();
      below = (e as DragEvent).clientY > r.top + r.height / 2;
    }
    await this.moveGroup(source.key, targetId, below);
  }
  handleDragEnd(): void { this._dragging = null; this._clearDropIndicator(); }

  // 그룹 이동 — 대상 날짜의 클러스터들을 distinct 간격으로 재배치(틈 만들기)하고
  // 그 사이(또는 위/아래)에 이동 그룹을 끼운다. 동일 created_at(일괄 생성)도 정확히 삽입 가능.
  private async moveGroup(groupKey: string, targetNeedId: string, below: boolean): Promise<void> {
    const effMs  = (x: api.SourcingListNeed) => new Date(x.sourcing_sort_at ?? x.created_at).getTime();
    const effIso = (x: api.SourcingListNeed) => x.sourcing_sort_at ?? x.created_at;

    const targetNeed = this.data.needs.find((n) => n.id === targetNeedId);
    if (!targetNeed) return;
    const D = effIso(targetNeed).slice(0, 10);                              // 대상 날짜
    const targetKey = targetNeed.sourcing_group_key ?? `solo:${targetNeed.id}`;

    // 대상 날짜의 클러스터(이동 그룹 제외), eff DESC
    type C = { key: string; needs: api.SourcingListNeed[]; eff: number };
    const byKey = new Map<string, C>();
    for (const n of this.data.needs) {
      const k = n.sourcing_group_key ?? `solo:${n.id}`;
      if (k === groupKey) continue;
      if (effIso(n).slice(0, 10) !== D) continue;
      const c = byKey.get(k);
      if (c) { c.needs.push(n); c.eff = Math.max(c.eff, effMs(n)); }
      else byKey.set(k, { key: k, needs: [n], eff: effMs(n) });
    }
    const dateClusters = [...byKey.values()].sort((a, b) => b.eff - a.eff);

    const movingNeeds = this.data.needs.filter((n) => n.sourcing_group_key === groupKey);
    if (movingNeeds.length === 0) return;
    const moving: C = { key: groupKey, needs: movingNeeds, eff: 0 };

    const tIdx = dateClusters.findIndex((c) => c.key === targetKey);
    const insertIdx = tIdx < 0 ? dateClusters.length : (below ? tIdx + 1 : tIdx);
    const ordered = [...dateClusters.slice(0, insertIdx), moving, ...dateClusters.slice(insertIdx)];

    // 그 날짜 안에서 distinct 내림차순 sort_at (1분 간격). 날짜 유지.
    const base = Date.parse(`${D}T23:00:00.000Z`);
    const assigned = ordered.map((c, i) => ({ c, iso: new Date(base - i * 60000).toISOString() }));
    const payload: api.SortBatchItem[] = assigned.map(({ c, iso }) =>
      c.key.startsWith('solo:') ? { need_id: c.key.slice(5), sort_at: iso } : { group_key: c.key, sort_at: iso });

    try {
      await api.setSortBatch(payload);
      const m = new Map<string, string>();
      for (const { c, iso } of assigned) for (const n of c.needs) m.set(n.id, iso);
      this.data.needs = this.data.needs.map((n) => (m.has(n.id) ? { ...n, sourcing_sort_at: m.get(n.id)! } : n));
      this.rerenderBody();
    } catch (err) {
      alert(`이동 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ── 묶기 확인 / 그룹 해제 ──
  async handleMergeApply(): Promise<void> {
    const m = this.data.mergeConfirm;
    this.data.mergeConfirm = null;
    if (!m) { this.rerenderBody(); return; }
    try {
      const src = m.source;   // 로컬 const 로 빼야 closure 안에서 narrowing 유지
      if (src.type === 'need') {
        await api.mergeNeedGroup(src.id, m.targetId);
      } else {
        // 그룹 전체 — 같은 key 니즈들을 순서대로 target 그룹에 합류(첫 호출이 target 키 확정).
        const key = src.key;
        const srcNeeds = this.data.needs.filter((n) => n.sourcing_group_key === key);
        for (const n of srcNeeds) await api.mergeNeedGroup(n.id, m.targetId);
      }
      await this.reload();
    } catch (err) {
      alert(`묶기 실패: ${err instanceof Error ? err.message : 'unknown'}`);
      this.rerenderBody();
    }
  }
  handleMergeCancel(): void {
    this.data.mergeConfirm = null;
    this.rerenderBody();
  }
  async handleGroupDissolve(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.needId;
    if (!id) return;
    try { await api.ungroupNeed(id, 'all'); await this.reload(); }
    catch (err) { alert(`그룹 해제 실패: ${err instanceof Error ? err.message : 'unknown'}`); }
  }
  async handleGroupRemove(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.needId;
    this.data.menuId = null;
    if (!id) return;
    try { await api.ungroupNeed(id, 'self'); await this.reload(); }
    catch (err) { alert(`그룹에서 빼기 실패: ${err instanceof Error ? err.message : 'unknown'}`); this.rerenderBody(); }
  }

  // ── 태그 편집 (영업요청/소싱메모 셀 클릭 → 체크박스 드롭다운) ──
  handleTagOpen(_e: Event, target: HTMLElement): void {
    const needId = target.dataset.needId;
    const kind   = target.dataset.kind as 'sales' | 'memo' | undefined;
    if (!needId || !kind) return;
    const r = target.getBoundingClientRect();
    this.data.tagEditor = { needId, kind, x: r.left, y: r.bottom + 4 };
    this.data.menuId = null;
    this.rerenderBody();
  }
  handleTagClose(): void {
    this.data.tagEditor = null;
    this.rerenderBody();
  }
  async handleTagToggle(_e: Event, target: HTMLElement): Promise<void> {
    const t = this.data.tagEditor;
    const tag = target.dataset.tag;
    if (!t || !tag) return;
    const need = this.data.needs.find((n) => n.id === t.needId);
    if (!need) return;
    const isSales = t.kind === 'sales';
    const cur = isSales ? need.sales_request_tags : need.sourcing_memo_tags;
    const next = cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag];
    // 로컬 즉시 반영 + 서버 저장
    if (isSales) need.sales_request_tags = next;
    else         need.sourcing_memo_tags = next;
    this.rerenderBody();
    try {
      await api.updateNeedTags(t.needId, isSales ? { sales_request_tags: next } : { sourcing_memo_tags: next });
    } catch (err) {
      alert(`태그 저장 실패: ${err instanceof Error ? err.message : 'unknown'}`);
      await this.reload();
    }
  }

  // ── 매입처 지정 모달 ──
  async handleSupplierOpen(_e: Event, target: HTMLElement): Promise<void> {
    const repId = target.dataset.needId;
    const rep   = this.data.needs.find((n) => n.id === repId);
    if (!rep) return;
    this.data.menuId = null;
    // 클러스터의 니즈 = 같은 group_key (없으면 단독)
    const needIds = rep.sourcing_group_key
      ? this.data.needs.filter((n) => n.sourcing_group_key === rep.sourcing_group_key).map((n) => n.id)
      : [rep.id];
    // 현재 배정된 매입처 = 클러스터 니즈들의 rfq supplier_id 합집합
    const assigned = new Set<string>();
    for (const n of this.data.needs) {
      if (!needIds.includes(n.id)) continue;
      for (const r of n.rfqs) assigned.add(r.supplier_id);
    }
    this.data.supplierModal = {
      ...emptySupplierModal(),
      open: true, needIds, label: `${needIds.length}건`,
      selected: [...assigned], loading: true,
    };
    this.rerenderSupplierModal();
    try {
      const all = await api.fetchActiveSuppliers();
      this.data.supplierModal.all = all;
    } catch (err) {
      this.data.supplierModal.error = err instanceof Error ? err.message : '매입처 목록 로드 실패';
    }
    this.data.supplierModal.loading = false;
    this.rerenderSupplierModal();
  }
  handleSupplierClose(): void {
    this.data.supplierModal.open = false;
    this.rerenderSupplierModal();
  }

  // ─── 개별 니즈 거래처 표시 설정(visibility) (마이그142) ─────────────────
  handleVisibilityOpen(_e: Event, target: HTMLElement): void {
    const needId = target.dataset.needId;
    const n = this.data.needs.find((x) => x.id === needId);
    if (!n) return;
    this.data.menuId = null;
    // 이 니즈에서 요청하는 품목(파트/설명). 그룹 매입처 = 이 니즈의 rfq 전체(숨김 포함).
    const item = n.option_part ?? n.item_text ?? n.need_no ?? '';
    this.data.visibilityModal = {
      ...emptyVisibilityModal(),
      open: true, needId: n.id, label: item, item,
      suppliers: n.rfqs.map((r) => {
        const cur   = r.price_currency === 'KRW' ? '₩' : '$';
        const price = r.price_amount != null ? `${cur}${r.price_amount.toLocaleString()}` : '';
        return {
          supplier_id:   r.supplier_id,
          supplier_name: r.supplier_name,
          visible:       !r.is_hidden,
          has_data:      r.price_amount != null || (r.sourced_condition ?? '') !== ''
                         || r.rfq_memo_tags.length > 0 || (r.alternative_part ?? '') !== '' || (r.request_note ?? '') !== '',
          status:        r.status,
          price,
        };
      }),
    };
    this.rerenderVisibilityModal();
  }
  handleVisibilityClose(): void {
    this.data.visibilityModal = emptyVisibilityModal();
    this.rerenderVisibilityModal();
  }
  handleVisibilitySearch(_e: Event, target: HTMLElement): void {
    this.data.visibilityModal.search = (target as HTMLInputElement).value;
    this.rerenderVisibilityModal();
  }
  handleVisibilityToggle(_e: Event, target: HTMLElement): void {
    const sid = target.dataset.supplierId;
    if (!sid) return;
    const s = this.data.visibilityModal.suppliers.find((x) => x.supplier_id === sid);
    if (s) s.visible = !s.visible;
    this.rerenderVisibilityModal();
  }
  async handleVisibilitySave(): Promise<void> {
    const m = this.data.visibilityModal;
    if (m.saving) return;
    m.saving = true; m.error = '';
    this.rerenderVisibilityModal();
    const visibleIds = m.suppliers.filter((s) => s.visible).map((s) => Number(s.supplier_id));
    try {
      await api.setNeedVisibility(m.needId, visibleIds);
      // 로컬 상태 갱신: 해당 니즈 rfq 의 is_hidden
      const n = this.data.needs.find((x) => x.id === m.needId);
      if (n) for (const r of n.rfqs) r.is_hidden = !visibleIds.includes(Number(r.supplier_id));
      this.data.visibilityModal = emptyVisibilityModal();
      this.rerenderBody();
      this.rerenderVisibilityModal();
    } catch (err) {
      m.saving = false;
      m.error  = err instanceof Error ? err.message : '저장 실패';
      this.rerenderVisibilityModal();
    }
  }
  handleSupplierSearch(_e: Event, target: HTMLElement): void {
    this.data.supplierModal.search = (target as HTMLInputElement).value;
    // 리스트만 갱신 — 검색 input DOM 은 그대로 둬 포커스 유지(전체 재렌더 시 포커스 날아감).
    // 리스트가 없는 상태(로딩/빈 목록)는 정상이라 element 를 먼저 찾고 넘김(selector 미매칭 warn 회피).
    const list = this._rootElement?.querySelector<HTMLElement>('.sourcing-supplier-list');
    if (list) this.refreshRegion(list, () => renderSupplierListInner(this.data.supplierModal));
    else this.rerenderSupplierModal();
  }
  // 선택 매입처 드래그 순서 변경 (§5.5.8)
  handleSupplierDragStart(e: Event, target: HTMLElement): void {
    this._supDragId = target.dataset.supplierId ?? null;
    (e as DragEvent).dataTransfer?.setData('text/plain', this._supDragId ?? '');
  }
  handleSupplierDragOver(e: Event): void { e.preventDefault(); }
  handleSupplierDragEnd(): void { this._supDragId = null; }
  handleSupplierDrop(e: Event, target: HTMLElement): void {
    e.preventDefault();
    const dragId = this._supDragId;
    const targetId = target.dataset.supplierId;
    this._supDragId = null;
    if (!dragId || !targetId || dragId === targetId) return;
    const sel = [...this.data.supplierModal.selected];
    const from = sel.indexOf(dragId);
    if (from < 0) return;
    sel.splice(from, 1);
    const to = sel.indexOf(targetId);
    sel.splice(to < 0 ? sel.length : to, 0, dragId);   // 대상 앞에 삽입
    this.data.supplierModal.selected = sel;
    this.rerenderSupplierModal();
  }
  handleSupplierToggle(_e: Event, target: HTMLElement): void {
    const id = target.dataset.supplierId;
    if (!id) return;
    const m = this.data.supplierModal;
    m.selected = m.selected.includes(id) ? m.selected.filter((s) => s !== id) : [...m.selected, id];
    this.rerenderSupplierModal();
  }
  async handleSupplierSave(): Promise<void> {
    const m = this.data.supplierModal;
    if (m.saving) return;
    // 삭제(체크 해제) 대상 중 RFQ 데이터 있는 매입처가 있으면 경고 — 확인해야 삭제(마이그142 정책 14).
    const selectedSet   = new Set(m.selected);
    const removedNames  = new Set<string>();
    for (const n of this.data.needs) {
      if (!m.needIds.includes(n.id)) continue;
      for (const r of n.rfqs) {
        if (selectedSet.has(r.supplier_id)) continue;   // 유지됨
        const hasData = r.price_amount != null || (r.sourced_condition ?? '') !== ''
          || r.rfq_memo_tags.length > 0 || (r.alternative_part ?? '') !== '' || (r.request_note ?? '') !== '';
        if (hasData) removedNames.add(r.supplier_name);
      }
    }
    if (removedNames.size > 0) {
      const ok = window.confirm(
        `삭제하는 매입처(${[...removedNames].join(', ')})에 RFQ 데이터(가격·컨디션·메모·대체품·태그)가 있습니다.\n저장하면 해당 데이터가 모두 삭제됩니다. 계속할까요?`,
      );
      if (!ok) return;
    }
    m.saving = true; m.error = '';
    this.rerenderSupplierModal();
    try {
      await api.syncClusterSuppliers(m.needIds, m.selected);
      this.data.supplierModal.open = false;
      this.rerenderSupplierModal();
      await this.reloadKeepingScroll();   // 첫 페이지 리셋/스크롤 튐 방지
    } catch (err) {
      m.saving = false;
      m.error  = err instanceof Error ? err.message : '저장 실패';
      this.rerenderSupplierModal();
    }
  }

  // ── 가격 응답 popover (rfq 버블 클릭) ──
  handlePriceOpen(_e: Event, target: HTMLElement): void {
    const rfqId = target.dataset.rfqId;
    if (!rfqId) return;
    let found: api.SourcingRfqBubble | undefined;
    let reqQty = 0;
    for (const n of this.data.needs) {
      const r = n.rfqs.find((x) => x.id === rfqId);
      if (r) { found = r; reqQty = n.quantity; break; }
    }
    if (!found) return;
    const rect = target.getBoundingClientRect();
    this.data.price = {
      ...emptyPrice(),
      open: true, rfqId,
      supplierName: found.supplier_name,
      amount:    found.price_amount != null ? String(found.price_amount) : '',
      currency:  found.price_currency ?? 'USD',
      noStock:   found.status === '재고 없음',
      condition: found.sourced_condition ?? '',
      respondedQty: (found as { responded_quantity?: number | null }).responded_quantity != null
        ? String((found as { responded_quantity?: number | null }).responded_quantity) : '',
      requestedQty: reqQty,
      altPart:   found.alternative_part ?? '',
      tags:      [...found.rfq_memo_tags],
      memo:      found.request_note ?? '',
      purchaseRequested: found.purchase_requested,
      attachments: [...found.attachments],
      x: rect.left, y: rect.bottom, anchorTop: rect.top, offX: 0, offY: 0,
    };
    this.rerenderPricePopover();
  }
  handlePriceClose(): void {
    this.data.price.open = false;
    this.rerenderPricePopover();
  }
  // 특정 거래처(RFQ) 구매요청 토글 (마이그140) — 🛒 표시 + 모달 버튼 갱신.
  async handlePricePurchaseToggle(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.rfqId;
    if (!id) return;
    const requested = target.dataset.requested !== '1';
    try {
      await api.setRfqPurchaseRequest(id, requested);
      for (const n of this.data.needs) {
        for (const r of n.rfqs) if (r.id === id) r.purchase_requested = requested;
      }
      if (this.data.price.rfqId === id) this.data.price.purchaseRequested = requested;
      this.rerenderBody();
      this.rerenderPricePopover();
    } catch (err) {
      alert(`구매 요청 처리 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ─── 거래처 수신 이미지/PDF 첨부 (마이그141) ───────────────────────────
  private _applyRfqAttachments(rfqId: string, attachments: api.SourcingRfqBubble['attachments']): void {
    this.data.price.attachments = attachments;
    for (const n of this.data.needs) {
      for (const r of n.rfqs) if (r.id === rfqId) r.attachments = attachments;
    }
  }
  async handlePriceImagePick(_e: Event, target: HTMLElement): Promise<void> {
    const input = target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    const rfqId = this.data.price.rfqId;
    if (files.length === 0 || !rfqId) return;
    this.data.price.uploading = true;
    this.rerenderPricePopover();
    try {
      const attachments = await api.uploadRfqImages(rfqId, files);
      this._applyRfqAttachments(rfqId, attachments);
    } catch (err) {
      alert(`첨부 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally {
      this.data.price.uploading = false;
      this.rerenderPricePopover();
      this.rerenderBody();
    }
  }
  async handlePriceImageDelete(_e: Event, target: HTMLElement): Promise<void> {
    const key   = target.dataset.key;
    const rfqId = this.data.price.rfqId;
    if (!key || !rfqId) return;
    try {
      const attachments = await api.deleteRfqImage(rfqId, key);
      this._applyRfqAttachments(rfqId, attachments);
      this.rerenderPricePopover();
      this.rerenderBody();
    } catch (err) {
      alert(`첨부 삭제 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }
  handlePriceField(_e: Event, target: HTMLElement): void {
    const el = target as HTMLInputElement | HTMLSelectElement;
    const key = el.dataset.field;
    if (key === 'amount')         this.data.price.amount = el.value;
    else if (key === 'currency')  this.data.price.currency = el.value;
    else if (key === 'condition') this.data.price.condition = el.value;
    else if (key === 'altPart')   this.data.price.altPart = el.value;
    else if (key === 'memo')      this.data.price.memo = el.value;
    else if (key === 'respondedQty') this.data.price.respondedQty = el.value;
    // 텍스트 입력은 재렌더 안 함(포커스 유지)
  }
  handlePriceStock(): void {
    this.data.price.noStock = !this.data.price.noStock;
    if (this.data.price.noStock) this.data.price.amount = '';
    this.rerenderPricePopover();
  }
  handlePriceTagsToggle(): void {
    this.data.price.tagsOpen = !this.data.price.tagsOpen;
    this.rerenderPricePopover();
  }
  handlePriceTag(_e: Event, target: HTMLElement): void {
    const tag = target.dataset.tag;
    if (!tag) return;
    const t = this.data.price.tags;
    this.data.price.tags = t.includes(tag) ? t.filter((x) => x !== tag) : [...t, tag];
    this.rerenderPricePopover();
  }
  // 대체품목 자동완성 — 입력 시 카탈로그 검색(디바운스), 결과 컨테이너만 갱신(포커스 유지).
  handlePriceAltInput(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value;
    this.data.price.altPart = q;
    if (this._altTimer) window.clearTimeout(this._altTimer);
    const term = q.trim();
    if (!term) {
      this.data.price.altResults = [];
      this.data.price.altSearching = false;
      this._updateAltResults();
      return;
    }
    this.data.price.altSearching = true;
    this._updateAltResults();
    this._altTimer = window.setTimeout(async () => {
      try {
        const res = await api.searchAll(term, 0, 8);
        if (this.data.price.altPart.trim() !== term) return;   // 더 새 입력에 양보
        this.data.price.altResults = res.catalog;
      } catch {
        this.data.price.altResults = [];
      }
      this.data.price.altSearching = false;
      this._updateAltResults();
    }, 250);
  }
  handlePriceAltPick(_e: Event, target: HTMLElement): void {
    const part = target.dataset.part;
    if (part == null) return;
    this.data.price.altPart = part;
    this.data.price.altResults = [];
    this.data.price.altSearching = false;
    this.rerenderPricePopover();
  }
  private _updateAltResults(): void {
    // popover 닫힘 상태의 미존재는 정상 — element 를 먼저 찾고 넘김.
    const box = this._priceLayer?.querySelector<HTMLElement>('#price-alt-results');
    if (box) this.refreshRegion(box, () => renderAltResults(this.data.price));
  }
  async handlePriceSave(): Promise<void> {
    const p = this.data.price;
    if (p.saving) return;
    p.saving = true; p.error = '';
    this.rerenderPricePopover();

    const amt = p.amount.trim() === '' ? null : Number(p.amount);
    const hasPrice = !p.noStock && amt != null && !Number.isNaN(amt);
    const patch: api.RfqResponsePatch = {
      status:            p.noStock ? '재고 없음' : (hasPrice ? '가격 수신' : '답변 대기'),
      price_amount:      hasPrice ? amt : null,
      price_currency:    hasPrice ? p.currency : null,
      sourced_condition: p.condition.trim() || null,
      responded_quantity: p.respondedQty.trim() === '' ? null : Number(p.respondedQty),
      alternative_part:  p.altPart.trim()   || null,
      rfq_memo_tags:     p.tags,
      request_note:      p.memo.trim()      || null,
    };
    try {
      await api.updateRfqResponse(p.rfqId, patch);
      // 로컬 버블 즉시 갱신
      for (const n of this.data.needs) {
        const idx = n.rfqs.findIndex((r) => r.id === p.rfqId);
        if (idx < 0) continue;
        n.rfqs[idx] = {
          ...n.rfqs[idx],
          status:            patch.status!,
          price_amount:      patch.price_amount ?? null,
          price_currency:    patch.price_currency ?? null,
          sourced_condition: patch.sourced_condition ?? null,
          responded_quantity: patch.responded_quantity ?? null,
          alternative_part:  patch.alternative_part ?? null,
          rfq_memo_tags:     patch.rfq_memo_tags ?? n.rfqs[idx].rfq_memo_tags,
        };
        break;
      }
      this.data.price.open = false;
      this.rerenderPricePopover();
      this.rerenderBody();
    } catch (err) {
      p.saving = false;
      p.error  = err instanceof Error ? err.message : '저장 실패';
      this.rerenderPricePopover();
    }
  }

  // ─── 핸들러 ───────────────────────────────────────────────────────
  handleSearchInput(_e: Event, target: HTMLElement): void {
    this.data.search = (target as HTMLInputElement).value;
    if (this._searchTimer) window.clearTimeout(this._searchTimer);
    this._searchTimer = window.setTimeout(() => { void this.reload(); }, 300);
  }

  handleMenuOpen(_e: Event, target: HTMLElement): void {
    const id = target.dataset.needId ?? null;
    this.data.menuId = this.data.menuId === id ? null : id;
    this.rerenderBody();
  }

  /** 행 복사 — "파트넘버 디스크립션 컨디션 수량EA" 형식(비-secure context fallback 포함). */
  async handleCopyDesc(_e: Event, target: HTMLElement): Promise<void> {
    const text = target.dataset.copy ?? '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // HTTPS/권한 없는 환경 fallback (execCommand 는 deprecated 지만 비-secure context 에서 동작)
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity  = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* 무시 */ }
      ta.remove();
    }
    // 짧은 시각 피드백(재렌더 없이 DOM 직접) — 클릭한 아이콘만 잠깐 강조.
    target.classList.add('is-copied');
    window.setTimeout(() => target.classList.remove('is-copied'), 800);
  }
  handleMenuClose(): void {
    this.data.menuId = null;
    this.rerenderBody();
  }
  async handleDelete(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.needId;
    this.data.menuId = null;
    if (!id) return;
    if (!confirm('이 니즈를 삭제할까요? (RFQ 이력은 보존됩니다)')) { this.rerenderBody(); return; }
    try {
      await api.deleteNeed(id);
      await this.reload();
    } catch (err) {
      alert(`삭제 실패: ${err instanceof Error ? err.message : 'unknown'}`);
      this.rerenderBody();
    }
  }

  // 니즈 구매(매입) 요청 토글 — 표시/플래그만. data-requested 로 현재값 판단.
  async handlePurchaseRequest(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.needId;
    this.data.menuId = null;
    if (!id) { this.rerenderBody(); return; }
    const requested = target.dataset.requested !== '1';   // 현재 0 → 요청, 1 → 취소
    try {
      await api.setNeedPurchaseRequest(id, requested);
      await this.reload();
    } catch (err) {
      alert(`구매 요청 처리 실패: ${err instanceof Error ? err.message : 'unknown'}`);
      this.rerenderBody();
    }
  }

  // ── 니즈 생성 모달 ──
  handleCreateOpen(): void {
    this.data.create = { ...emptyCreate(), open: true };
    this.rerenderModal();
  }
  handleCreateClose(): void {
    this.data.create.open = false;
    this.rerenderModal();
  }
  handleCreateField(_e: Event, target: HTMLElement): void {
    const el  = target as HTMLInputElement | HTMLSelectElement;
    const key = el.dataset.field as keyof CreateState | undefined;
    if (!key) return;
    // 문자열 필드만 (태그는 별도)
    (this.data.create as unknown as Record<string, unknown>)[key] = el.value;
  }
  handleCreateTag(_e: Event, target: HTMLElement): void {
    const kind = target.dataset.kind;   // 'sales' | 'memo'
    const tag  = target.dataset.tag;
    if (!tag) return;
    const arr  = kind === 'memo' ? this.data.create.memoTags : this.data.create.salesTags;
    const next = arr.includes(tag) ? arr.filter((t) => t !== tag) : [...arr, tag];
    if (kind === 'memo') this.data.create.memoTags = next;
    else                 this.data.create.salesTags = next;
    this.rerenderModal();
  }

  // ── 품목 검색 (250ms 디바운스, 결과 컨테이너만 갱신 — 입력 포커스 보존) ──
  handleCreateSearch(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value;
    this.data.create.query = q;
    if (this._createSearchTimer) window.clearTimeout(this._createSearchTimer);
    if (q.trim().length < 2) {
      this.data.create.hits = [];
      this.data.create.searching = false;
      this.updateCreateSearchResults();
      return;
    }
    this._createSearchTimer = window.setTimeout(() => { void this.runCreateSearch(q); }, 250);
  }
  private async runCreateSearch(q: string): Promise<void> {
    this.data.create.searching = true;
    this.updateCreateSearchResults();
    try {
      const res = await api.searchAll(q, 0, 20);
      if (this.data.create.query !== q) return;   // 최신 검색어만 반영
      this.data.create.hits = res.catalog ?? [];
    } catch {
      this.data.create.hits = [];
    } finally {
      if (this.data.create.query === q) {
        this.data.create.searching = false;
        this.updateCreateSearchResults();
      }
    }
  }
  private updateCreateSearchResults(): void {
    const box = this._rootElement?.querySelector('#sourcing-need-search-results');
    if (box) box.innerHTML = renderCreateSearchResultsHtml(this.data.create);
  }
  handleCreatePick(_e: Event, target: HTMLElement): void {
    const key = target.dataset.key;
    if (!key) return;
    const hit = this.data.create.hits.find((h) => `${h.source}::${h.option_part}` === key);
    if (!hit) return;
    const c = this.data.create;
    c.optionPart = hit.option_part;
    c.itemText   = catalogHitDescription(hit);
    c.category   = hit.source ?? c.category;
    c.query = ''; c.hits = []; c.searching = false;
    this.rerenderModal();
  }
  handleCreateClear(): void {
    const c = this.data.create;
    c.optionPart = ''; c.itemText = ''; c.category = ''; c.query = ''; c.hits = [];
    this.rerenderModal();
  }
  async handleCreateSubmit(): Promise<void> {
    const c = this.data.create;
    if (c.saving) return;
    if (!c.optionPart.trim() && !c.itemText.trim()) {
      c.error = '파트넘버 또는 품목명 중 하나는 입력하세요.';
      this.rerenderModal();
      return;
    }
    c.saving = true; c.error = '';
    this.rerenderModal();
    try {
      await api.createNeed({
        deal_id:             null,   // Phase 1 — 독립 니즈
        option_part:         c.optionPart.trim() || null,
        item_text:           c.itemText.trim() || null,
        category:            c.category.trim() || null,
        quantity:            Math.max(1, parseInt(c.quantity, 10) || 1),
        requested_condition: c.condition || null,
        sales_request_tags:  api.toSalesTags(c.salesTags),
        sourcing_memo_tags:  api.toMemoTags(c.memoTags),
      });
      this.data.create.open = false;
      this.rerenderModal();
      await this.reload();
    } catch (err) {
      c.saving = false;
      c.error  = err instanceof Error ? err.message : '생성 실패';
      this.rerenderModal();
    }
  }

  // ─── 렌더 ─────────────────────────────────────────────────────────
  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page sourcing-page">
        <header class="sourcing-topbar">
          <div class="sourcing-topbar-row">
            <h1>Sourcing</h1>
            <button class="btn btn-primary" type="button" data-action="create-open">+ 니즈 생성</button>
          </div>
          <input class="input sourcing-search-input" type="text" data-action="search-input"
                 placeholder="제품명 또는 파트넘버 검색" value="${escapeHtml(this.data.search)}">
        </header>
        <div class="sourcing-body">${renderBody(this.data)}</div>
        <div class="sourcing-modal-wrap">${renderCreateModal(this.data.create)}</div>
        <div class="sourcing-supplier-modal-wrap">${renderSupplierModal(this.data.supplierModal)}</div>
        <div class="sourcing-visibility-modal-wrap">${renderVisibilityModal(this.data.visibilityModal)}</div>
      </div>
    `;
  }
}
